import { useEffect, type RefObject } from 'react';
import type { NavigateFunction } from 'react-router-dom';
import { destroyWebflowRuntime, initWebflowRuntime } from '../lib/loadWebflowRuntime';
import { setupScrollMotion } from '../lib/scrollMotion';

function on(el: Element | Document | Window, event: string, handler: EventListener, options?: AddEventListenerOptions) {
  el.addEventListener(event, handler, options);
  return () => el.removeEventListener(event, handler, options);
}

function setupVideos(root: HTMLElement) {
  root.querySelectorAll<HTMLVideoElement>('video').forEach((video) => {
    video.muted = true;
    video.playsInline = true;
    void video.play().catch(() => undefined);
  });
}

function setupForms(root: HTMLElement): () => void {
  const form = root.querySelector<HTMLFormElement>('.newsletter-form');
  if (!form) return () => undefined;

  return on(form, 'submit', (event) => {
    event.preventDefault();
    const done = form.parentElement?.querySelector<HTMLElement>('.w-form-done');
    if (done) done.style.display = 'block';
    form.style.display = 'none';
  });
}

function setupInternalLinks(root: HTMLElement, navigate: NavigateFunction): () => void {
  const handler = (event: Event) => {
    const target = event.target as HTMLElement | null;
    const link = target?.closest<HTMLAnchorElement>('a[href^="/"]');
    if (!link) return;

    const path = link.getAttribute('href');
    if (!path || path.startsWith('//')) return;

    if (
      link.target === '_blank' ||
      (event instanceof MouseEvent && (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey))
    ) {
      return;
    }

    event.preventDefault();
    navigate(path);
  };

  return on(root, 'click', handler);
}

export function useSiteInteractions(
  rootRef: RefObject<HTMLElement | null>,
  navigate: NavigateFunction,
  pathname: string,
) {
  useEffect(() => {
    const root = rootRef.current;
    if (!root) return;

    document.body.classList.add('site-page');
    setupVideos(root);

    let cancelled = false;

    void initWebflowRuntime()
      .then(() => {
        if (cancelled) return;
        setupVideos(root);
      })
      .catch(() => {
        /* Webflow CDN blocked - scrollMotion.ts handles in-view reveals */
      });

    const cleanups = [setupForms(root), setupInternalLinks(root, navigate)];

    return () => {
      cancelled = true;
      document.body.classList.remove('site-page', 'site-nav-open');
      destroyWebflowRuntime();
      cleanups.forEach((cleanup) => cleanup());
    };
  }, [rootRef, navigate]);

  useEffect(() => {
    let cleanupMotion: () => void = () => {};
    const frame = requestAnimationFrame(() => {
      const root = rootRef.current;
      if (!root) return;
      cleanupMotion = setupScrollMotion(root);
    });

    return () => {
      cancelAnimationFrame(frame);
      cleanupMotion();
    };
  }, [rootRef, pathname]);
}
